import React, { useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import type { ConfirmDialogProps } from './ConfirmDialog';
import type { PendingConfirmation, BackgroundJobResult } from '../../hooks/useActionExecutor';
import { resolveTemplate } from '../../utils/interpolation';

interface PendingSaveConfirm {
  config: {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'danger' | 'warning' | 'info';
    titleAlign?: 'left' | 'center' | 'right';
    messageAlign?: 'left' | 'center' | 'right';
  };
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface TableDialogsProps {
  pendingConfirm: PendingConfirmation | null;
  setPendingConfirm: (confirm: PendingConfirmation | null) => void;
  pendingSaveConfirm: PendingSaveConfirm | null;
  pendingBackgroundJob: BackgroundJobResult | null;
  setPendingBackgroundJob: (job: BackgroundJobResult | null) => void;
  effectiveTotalCount: number;
  confirmRenderer?: (props: ConfirmDialogProps) => React.ReactNode;
  defaultNavigate: (path: string, options?: { state?: Record<string, unknown> }) => void;
}

export function TableDialogs({
  pendingConfirm,
  setPendingConfirm,
  pendingSaveConfirm,
  pendingBackgroundJob,
  setPendingBackgroundJob,
  effectiveTotalCount,
  confirmRenderer,
  defaultNavigate,
}: TableDialogsProps): JSX.Element {
  const [confirmLoading, setConfirmLoading] = useState(false);

  return (
    <>
      {pendingConfirm &&
        pendingConfirm.action.dialog &&
        (() => {
          const ctx = pendingConfirm.context;
          const tctx = {
            row: ctx.row,
            selectedKeys: ctx.selectedKeys,
            selectAllActive: ctx.selectAllActive,
            deselectedRowIds: ctx.deselectedRowIds,
            totalCount: effectiveTotalCount ?? ctx.totalCount,
            tableQuery: ctx.tableQuery,
          };

          const dlg = pendingConfirm.action.dialog;
          const resolvedTitle = resolveTemplate(dlg.title, tctx);
          const resolvedMessage = resolveTemplate(dlg.message, tctx);
          const confirmLabel = dlg.confirmLabel;
          const cancelLabel = dlg.cancelLabel;
          const variant = dlg.variant as 'danger' | 'warning' | 'info';
          const dialogKind = dlg.kind;
          const handleConfirm = async () => {
            setConfirmLoading(true);
            try {
              await pendingConfirm.onConfirm();
            } finally {
              setConfirmLoading(false);
              setPendingConfirm(null);
            }
          };
          const handleCancel = () => {
            pendingConfirm.onCancel();
            setPendingConfirm(null);
          };

          return confirmRenderer ? (
            confirmRenderer({
              title: resolvedTitle,
              message: resolvedMessage,
              confirmLabel,
              cancelLabel,
              variant,
              dialogKind,
              onConfirm: handleConfirm,
              onCancel: handleCancel,
              isLoading: confirmLoading,
            })
          ) : (
            <ConfirmDialog
              title={resolvedTitle}
              message={resolvedMessage}
              confirmLabel={confirmLabel}
              cancelLabel={cancelLabel}
              variant={variant}
              dialogKind={dialogKind}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
              isLoading={confirmLoading}
            />
          );
        })()}

      {pendingSaveConfirm &&
        (() => {
          const { config: sc, onConfirm: scConfirm, onCancel: scCancel, isLoading: scLoading } = pendingSaveConfirm;
          return confirmRenderer ? (
            confirmRenderer({
              title: sc.title,
              message: sc.message,
              confirmLabel: sc.confirmLabel ?? 'Confirm',
              cancelLabel: sc.cancelLabel ?? 'Cancel',
              variant: sc.variant ?? 'warning',
              dialogKind: 'confirm',
              onConfirm: scConfirm,
              onCancel: scCancel,
              isLoading: scLoading,
              titleAlign: sc.titleAlign,
              messageAlign: sc.messageAlign,
            })
          ) : (
            <ConfirmDialog
              title={sc.title}
              message={sc.message}
              confirmLabel={sc.confirmLabel ?? 'Confirm'}
              cancelLabel={sc.cancelLabel ?? 'Cancel'}
              variant={sc.variant ?? 'warning'}
              dialogKind='confirm'
              onConfirm={scConfirm}
              onCancel={scCancel}
              isLoading={scLoading}
              titleAlign={sc.titleAlign}
              messageAlign={sc.messageAlign}
            />
          );
        })()}

      {pendingBackgroundJob && (() => {
        const bj = pendingBackgroundJob;
        const handleBjConfirm = () => {
          if (bj.navigatePath) {
            defaultNavigate(bj.navigatePath);
          }
          setPendingBackgroundJob(null);
        };
        return (
          <ConfirmDialog
            title={bj.title}
            message={
              bj.showTaskReferenceNo && bj.taskReferenceNo
                ? `${bj.message}\n\nTask Reference: ${bj.taskReferenceNo}`
                : bj.message
            }
            confirmLabel={bj.confirmLabel ?? 'OK'}
            cancelLabel='Close'
            variant='info'
            dialogKind='alert'
            onConfirm={handleBjConfirm}
            onCancel={() => setPendingBackgroundJob(null)}
          />
        );
      })()}
    </>
  );
}
